<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'
import { useAuthStore } from '@/stores/auth.store'
import Menubar from 'primevue/menubar'
import Menu from 'primevue/menu';
import Button from 'primevue/button';
import type { MenuItem } from 'primevue/menuitem'
import { UIApi, Configuration } from '@/api/javaapi'
import { gwtBridge } from '@/GWTBridge';
import type { NavigationFailure } from 'vue-router';

const authStore = useAuthStore();

const menuItems = ref<MenuItem[]>([]);

const icons = ref<MenuItem[]>([]);
const menubar = ref();
const userMenu = ref();
const userMenuItems = ref<MenuItem[]>([
  { label: 'Notification Settings', icon: 'pi pi-bell', to: '/notification-settings', },
  { label: "Support", icon: 'pi pi-question-circle', url: 'https://bookonthebrightside.com/kb/', target: '_blank' },
  { label: 'Logout', icon: 'pi pi-sign-out', command: () => { authStore.logout() } }
]);
const toggle = (event: Event): void => {
  userMenu.value?.toggle(event);
}
const closeMenus = (event: FocusEvent) => {
  if (!event.relatedTarget) {
    menubar.value?.hide();
  }
};
var bridge: any;

gwtBridge.then((retBridge) => {
  bridge = retBridge;
});

const handleNavigation = (
  navigate?: (event?: MouseEvent | undefined) => Promise<void | NavigationFailure>,
  event?: MouseEvent
) => {
  gwtBridge.then((bridge) => {
    bridge.forceReload();
  });
  if (navigate) return navigate(event);
};

const refreshMenu = (token: string) => {
  let uiApi = new UIApi(new Configuration({ accessToken: token }));
  uiApi.getMenu()
    .then(response => {
      menuItems.value = response.data;
      const iconsToAdd: Record<string, string> = {
        "New Reservation": "pi pi-plus",
        "Unit Calendar": "pi pi-calendar",
        "Reservation Search": "pi pi-search"
      };
      icons.value.push({ label: "Home", icon: 'pi pi-home', route: '/rm4/Login.php' });
      const addIconsToMenuItems = (items: MenuItem[]) => {
        items.forEach(item => {
          if (typeof item.label === 'string' && iconsToAdd[item.label]) {
            item.icon = iconsToAdd[item.label];
            icons.value.push(item);
          }
          if (item.items) {
            addIconsToMenuItems(item.items);
          }
        });
      };

      addIconsToMenuItems(menuItems.value);

      icons.value.sort((a, b) => {
        return Object.keys(iconsToAdd).indexOf(a.label as string) - Object.keys(iconsToAdd).indexOf(b.label as string);
      });
    })
    .catch(error => {
      console.error('Failed to load menu items:', error)
    })
}
onMounted(() => {
  menubar.value?.$el.addEventListener('focusout', closeMenus);
  gwtBridge.then((bridge) => {
    bridge.addScreenChangeHandler((refresh: boolean) => {
      if (refresh && authStore.token) {
        icons.value = [];
        refreshMenu(authStore.token)
      }
    });
  });
});
watch(() => authStore.token, (newVal) => {
  if (newVal) {
    refreshMenu(newVal);
  } else {
    menuItems.value = [];
    icons.value = [];
  }
}, { immediate: true })
</script>

<template>
  <header id="appHeader">
    <Menubar :model="menuItems" breakpoint="1179px" :onblur="closeMenus" ref="menubar">
      <template #start>
        <router-link :to="{path: '/rm4/Login.php', force: true}" class="hidden md:inline-block"><img src="@/assets/images/icon.svg" alt="BrightSide" title="Go to Dashboard" width="40" height="40"></router-link>
      </template>
      <template #item="{ item, props, hasSubmenu, root }">
        <router-link v-if="item.route" v-slot="{ href, navigate, isActive }" :to="item.route" custom>
          <a v-ripple :href="href" v-bind="props.action" @click="handleNavigation(navigate, $event)" :class="{ 'router-link-active': isActive }">
            <span>{{ item.label }}</span>
          </a>
        </router-link>
        <a v-else v-ripple :href="item.url" :target="item.target" v-bind="props.action">
          <span>{{ item.label }}</span>
          <i v-if="hasSubmenu" :class="['pi ml-auto', { 'pi-angle-down': root, 'pi-angle-right': !root }]"></i>
        </a>
      </template>
      <template #end>
        <div class="flex gap-2" v-if="authStore.token">
          <div v-for="icon in icons">
            <router-link :to="{path: icon.route, force: true}">
              <Button :icon="icon.icon" :title="(icon.label as string)" rounded variant="text" severity="contrast" />
            </router-link>
          </div>
          <Button type="button" icon="pi pi-user" rounded @click="toggle" aria-haspopup="true" aria-controls="user_menu"
            variant="text" severity="contrast" />
          <Menu ref="userMenu" id="user_menu" :model="userMenuItems" :popup="true">
            <template #item="{ item }">
              <!-- Handle Vue Router navigation with 'to' -->
              <RouterLink v-if="item.to" :to="item.to" class="p-menuitem-link flex items-center" custom
                v-slot="{ href, navigate }">
                <a :href="href" @click="navigate" class="flex items-center p-2" v-ripple>
                  <span :class="item.icon" class="mr-2" />
                  <span>{{ item.label }}</span>
                </a>
              </RouterLink>

              <!-- Handle external URLs -->
              <a v-else-if="item.url" :href="item.url" :target="item.target"
                class="p-menuitem-link flex items-center p-2" v-ripple>
                <span :class="item.icon" class="mr-2" />
                <span>{{ item.label }}</span>
              </a>

              <!-- Handle commands (default behavior) -->
              <a v-else href="#" class="p-menuitem-link flex items-center p-2" @click="item.command && item.command({ originalEvent: $event, item })" v-ripple>
                <span :class="item.icon" class="mr-2" />
                <span>{{ item.label }}</span>
              </a>
            </template>
          </Menu>
        </div>
      </template>
    </Menubar>
  </header>
</template>
<style scoped>
#appHeader {
  --p-menubar-background: #eef4f8 url(@/assets/images/header-bg.png) repeat-x left/cover;
  --p-menubar-base-item-padding: 5px;
  --p-menubar-item-gap: 5px;
  --p-menubar-border-radius: 0;
  --p-menubar-submenu-padding: 0px;
  --p-menubar-submenu-gap: 0;

  @media screen and (min-width: 1180px) {
    --p-menubar-item-padding: .25em .5em;

  }

  --p-menubar-submenu-border-radius: 0;
  border-bottom: 1px solid grey;
}

#appHeader .p-menubar {
  gap: 1rem;
}

:deep(.p-menubar-mobile .p-menubar-root-list) {
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  flex-wrap: nowrap;
}

html.bs-dark #appHeader {
  --p-menubar-background: var(--p-content-background);
}

:deep(.p-menubar) {
  border: 0;
}

:deep(.pi-angle-down) {
  display: none;
}

:deep(.pi-angle-right) {
  display: inline-block;
}

:deep(.p-menubar-mobile .pi-angle-down) {
  display: inline-block;
}

:deep(.p-menubar-item) {
  font-weight: 500;
}

:deep(.p-menubar-submenu) {
  z-index: 2;
}

:deep(.p-menubar-submenu .p-menubar-item) {
  font-weight: normal;
  text-wrap-mode: nowrap;
}

@media print {
  #appHeader {
    display: none;
  }
}
</style>